// src/components/Dashboard/RoleDashboard/RoleExtras/Bookmarks.jsx

import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { BASE_URL } from "../../../../config";
import { useNavigate } from "react-router-dom";
import "./ViewApplications.css";
import { FaStar } from "react-icons/fa";

const VALID_ROLES = ["agent", "scout", "club"];

function getCurrentRole() {
    if (typeof window === "undefined") return "agent";
    const raw = (localStorage.getItem("userRole") || "").toLowerCase();
    if (!VALID_ROLES.includes(raw)) return "agent";
    return raw;
}

function getRoleId(role) {
    if (typeof window === "undefined") return null;

    const keyByRole = {
        agent: "agentId",
        scout: "scoutId",
        club: "clubId",
    };

    const key = keyByRole[role] || "agentId";
    return localStorage.getItem(key);
}

const Bookmarks = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const role = getCurrentRole();
    const roleId = getRoleId(role);

    // ✅ Role-aware backend fetch + newest-first ordering
    useEffect(() => {
        if (!roleId) {
            console.error(`❌ No ${role} id found in localStorage`);
            setLoading(false);
            return;
        }

        const fetchStarredApplications = async () => {
            setLoading(true);
            try {
                const response = await apiFetch(`/api/starred-applications/${role}/${roleId}`, {
                    method: "GET",
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => "");
                    throw new Error(
                        `Failed to fetch bookmarks (${response.status}): ${text.slice(
                            0,
                            200
                        )}`
                    );
                }

                const data = await response.json();
                console.log("[Bookmarks] received:", data);

                // ✅ NEW: sort by most recent first
                const sorted = Array.isArray(data)
                    ? [...data].sort(
                        (a, b) => new Date(b.appliedAt) - new Date(a.appliedAt)
                    )
                    : [];

                setApplications(sorted);
            } catch (error) {
                console.error("Error fetching starred applications:", error);
                setApplications([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStarredApplications();
    }, [role, roleId]);

    const formatLink = (url) => {
        if (!url) return "#";
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    };

    const toAbsoluteUrl = (maybePathOrUrl) => {
        if (!maybePathOrUrl) return "#";

        // already absolute
        if (/^https?:\/\//i.test(maybePathOrUrl)) return maybePathOrUrl;

        // safely join relative/path to BASE_URL
        try {
            return new URL(maybePathOrUrl, BASE_URL).toString();
        } catch {
            return "#";
        }
    };

    return (
        <div
            className={`bookmarks-fullscreen-root ${role}-view-applications`}
            style={{
                width: "100vw",
                minHeight: "100vh",
                backgroundColor: "#000",
                color: "#fff",
                padding: "16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
            }}
        >
            <div
                className="applications-container"
                style={{
                    width: "100%",
                    maxWidth: "1100px",
                    marginTop: "0",
                    background: "transparent",
                }}
            >
                <h2
                    className="applications-title"
                    style={{
                        color: "#FFFF00",
                        textAlign: "center",
                        width: "100%",
                        margin: "12px 0 8px 0",
                        fontSize: "2.4rem",
                        fontWeight: "900",
                    }}
                >
                    Bookmarked Applications
                </h2>

                {!loading && applications.length === 0 && (
                    <p
                        className="no-applications-text"
                        style={{
                            color: "#FFFFFF",
                            textAlign: "center",
                            marginBottom: "24px",
                            fontSize: "1rem",
                            opacity: 0.9,
                        }}
                    >
                        No bookmarked applications yet.
                    </p>
                )}

                {loading ? (
                    <p
                        className="loading-text"
                        style={{
                            color: "#FFFFFF",
                            textAlign: "center",
                            width: "100%",
                            margin: "0 auto",
                            fontSize: "1rem",
                            opacity: 0.9,
                        }}
                    >
                        Loading...
                    </p>
                ) : (
                    applications.map((app, index) => (
                        <div className="application-card" key={app.applicationId || index}>
                            <div className="application-header">
                                <div className="application-number">
                                    <FaStar style={{ marginRight: "8px" }} />
                                    Application #{applications.length - index}
                                </div>
                            </div>

                            {app.opportunity && (
                                <>
                                    <p className="application-field">
                                        <strong>Posted Opportunity:</strong>{" "}
                                        {app.opportunity.description}
                                    </p>

                                    {app.opportunity.link && (
                                        <p className="application-field">
                                            <strong>Link:</strong>{" "}
                                            <a
                                                href={formatLink(app.opportunity.link)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {app.opportunity.link}
                                            </a>
                                        </p>
                                    )}

                                    <p className="application-field">
                                        <strong>Status:</strong>{" "}
                                        {app.opportunity.status || "Open"}
                                    </p>
                                </>
                            )}

                            <p className="application-field">
                                <strong>Application:</strong> {app.introduction}
                            </p>

                            <p className="application-field">
                                <strong>Profile:</strong>{" "}
                                <button
                                    className="view-profile-btn"
                                    onClick={() =>
                                        navigate(`/player-profile/${app.playerId}`, {
                                            state: {
                                                viewer: role,
                                                playerId: app.playerId,
                                            },
                                        })
                                    }
                                >
                                    View Profile
                                </button>
                            </p>

                            {app.cvUrl && (
                                <p className="application-field">
                                    <strong>CV:</strong>{" "}
                                    <a
                                        href={toAbsoluteUrl(app.cvUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download CV
                                    </a>
                                </p>
                            )}

                            {app.appliedAt && (
                                <p className="application-date">
                                    Applied at:{" "}
                                    {new Date(app.appliedAt).toLocaleString()}
                                </p>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Bookmarks;








